import { computed } from 'vue'
import { isFunction } from '@/global/utils/utils'
import { useActiveStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useActiveStatus'
import { useResponsiblePerson } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useResponsiblePerson'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { BASES_AUTOCOMPLETE_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'
import { SUBCONTRACTORS_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/subcontractors/config/constants'

const COMMENT_ROWS = 6

/**
 * The one field list behind both the details and the create screen, so the two
 * can never describe a subcontractor differently.
 */
export function useSubcontractorFields (options) {
  const { translate, openGeolocationDialogFn } = options || {}

  const { statusItems } = useActiveStatus({ trans: translate })
  const {
    responsiblePersonField,
    responsiblePersonEmailField,
    responsiblePersonPhoneField
  } = useResponsiblePerson()

  // The address is picked on a map, the same way every other module does it -
  // the dialog writes the address back together with the coordinates the map
  // panel then renders its marker from.
  const onOpenGeolocationDialog = (event) => {
    if (!isFunction(openGeolocationDialogFn)) {
      return
    }

    openGeolocationDialogFn(event)
  }

  const fields = computed(() => ([
    {
      key: 'name',
      label: translate(`${NS}.company_name`),
      creatable: true,
      editable: true,
      visible: true
    },
    {
      key: 'oib',
      label: translate(`${NS}.oib`),
      creatable: true,
      editable: true,
      visible: true
    },
    {
      key: 'address',
      label: translate(`${NS}.address`),
      appendIcon: 'mdi-map-marker',
      appendIconClick: onOpenGeolocationDialog,
      creatable: true,
      editable: true,
      visible: true
    },
    {
      // Which base the subcontractor works out of. A subcontractor has exactly one, and
      // it is what its operators reach the base through - so it is set here rather than
      // on every vehicle the subcontractor brings.
      key: 'base_id',
      label: translate(`${NS}.default_base`),
      type: 'autocomplete',
      autocomplete_options: {
        module: WINTER_SERVICE_MODULE,
        route: BASES_AUTOCOMPLETE_ENDPOINT
      },
      creatable: true,
      editable: true,
      visible: true
    },
    {
      key: 'status',
      label: translate(`${NS}.status`),
      type: 'autocomplete',
      items: statusItems.value,
      visible: (fieldsRenderer) => !fieldsRenderer?.createMode,
      creatable: true,
      editable: true
    },
    {
      key: 'comment',
      label: translate(`${NS}.comment`),
      type: 'textarea',
      rows: COMMENT_ROWS,
      creatable: true,
      editable: true,
      visible: true
    },
    // Typed, all three of them - the contact is a snapshot rather than a link to
    // someone in the people register. See useResponsiblePerson.
    responsiblePersonField(translate(`${NS}.responsible_person`)),
    responsiblePersonEmailField(translate(`${NS}.responsible_person_email`)),
    responsiblePersonPhoneField(translate(`${NS}.responsible_person_phone`))
  ]))

  return {
    fields
  }
}
